/**
 * The Smart Pen's scorers for the shapes drawn in one open sweep or one round
 * loop: straight line, arrow, circle, ellipse (SRS C5 shape library).
 *
 * Every scorer answers the same question — how much does this stroke look like
 * the shape I am — as a number from 0 to 1, so the recogniser can simply take
 * the highest. None of them decide anything; `shape-recognise.ts` does that.
 */

import type { ShapeMatch } from '@opal/contracts';
import type { Point } from '@opal/shared';

import {
  distance,
  findCorners,
  meanDistanceTo,
  normaliseAngle,
  outsideBand,
  pathLength,
  principalAxes,
  scoreFromError,
  type StrokePath,
} from './recognise-geometry.js';

/** A turn this sharp reads as a corner rather than a curve. About 43 degrees. */
export const CORNER_MIN_TURN = 0.75;

/** Beyond this stretch a round shape is an ellipse rather than a circle. */
const CIRCLE_MAX_ASPECT = 1.18;

/** An arrow's tip sits this far along the stroke: shaft first, head last. */
const TIP_AT_LEAST = 0.5;
const TIP_AT_MOST = 0.92;

/** A straight line: it goes where it is pointing and does not wander. */
export function scoreLine(path: StrokePath): ShapeMatch | null {
  const start = path.even[0];
  const end = path.even[path.even.length - 1];
  if (!start || !end) return null;

  const chord = distance(start, end);
  if (chord <= 0) return null;

  const directness = scoreFromError(path.length / chord - 1, 0.02, 0.22);
  const tightness = scoreFromError(meanDistanceTo(path.even, [start, end], false) / chord, 0.012, 0.075);
  const confidence = directness * tightness;
  if (confidence <= 0) return null;

  return { shapeType: 'line', confidence, geometry: { points: [start, end] } };
}

/** The sample that ends up furthest from where the stroke began. */
function farthestIndex(points: readonly Point[], from: Point): number {
  let best = 0;
  let bestDistance = -1;
  for (let index = 0; index < points.length; index += 1) {
    const point = points[index];
    if (!point) continue;
    const away = distance(point, from);
    if (away > bestDistance) {
      bestDistance = away;
      best = index;
    }
  }
  return best;
}

/**
 * An arrow: a straight shaft out to a tip, then a head drawn back over itself.
 *
 * The head is what separates an arrow from a line, and doubling back is what
 * separates it from a bent line, so both are scored rather than assumed.
 */
export function scoreArrow(path: StrokePath): ShapeMatch | null {
  if (path.closed) return null;

  const start = path.even[0];
  const finish = path.even[path.even.length - 1];
  if (!start || !finish) return null;

  const tipIndex = farthestIndex(path.even, start);
  const share = tipIndex / (path.even.length - 1);
  if (share < TIP_AT_LEAST || share > TIP_AT_MOST) return null;

  const tip = path.even[tipIndex];
  if (!tip) return null;

  const shaft = path.even.slice(0, tipIndex + 1);
  const head = path.even.slice(tipIndex);
  const shaftLength = pathLength(shaft);
  const reach = distance(start, tip);
  if (shaftLength <= 0 || reach <= 0) return null;

  const straightness = scoreFromError(meanDistanceTo(shaft, [start, tip], false) / reach, 0.02, 0.09);
  const proportion = scoreFromError(outsideBand(pathLength(head) / shaftLength, 0.14, 1), 0, 0.3);
  const spread = Math.max(...head.map((point) => distance(point, tip))) / shaftLength;
  const compactness = scoreFromError(outsideBand(spread, 0.04, 0.5), 0, 0.25);

  const shaftAngle = Math.atan2(tip.y - start.y, tip.x - start.x);
  const backAngle = Math.atan2(finish.y - tip.y, finish.x - tip.x);
  const turnedBack = Math.abs(normaliseAngle(backAngle - shaftAngle));
  const reversal = scoreFromError(outsideBand(turnedBack, 1.75, Math.PI), 0, 0.9);

  const confidence = straightness * proportion * compactness * reversal;
  if (confidence <= 0) return null;

  return { shapeType: 'arrow', confidence, geometry: { points: [start, tip] } };
}

/** The four points where a clean round shape meets its own axes. */
function axisPoints(centre: Point, major: Point, minor: Point, radiusX: number, radiusY: number): Point[] {
  return [
    { x: centre.x + major.x * radiusX, y: centre.y + major.y * radiusX },
    { x: centre.x + minor.x * radiusY, y: centre.y + minor.y * radiusY },
    { x: centre.x - major.x * radiusX, y: centre.y - major.y * radiusX },
    { x: centre.x - minor.x * radiusY, y: centre.y - minor.y * radiusY },
  ];
}

/**
 * A circle or an ellipse: a closed loop that never corners and keeps the same
 * distance from its centre, once that distance is allowed to differ between the
 * two axes.
 */
export function scoreEllipse(path: StrokePath): ShapeMatch | null {
  if (!path.closed) return null;

  const axes = principalAxes(path.loop);
  if (axes.halfMajor <= 0 || axes.halfMinor <= 0) return null;

  let error = 0;
  for (const point of path.loop) {
    const dx = point.x - axes.centre.x;
    const dy = point.y - axes.centre.y;
    const u = (dx * axes.major.x + dy * axes.major.y) / axes.halfMajor;
    const v = (dx * axes.minor.x + dy * axes.minor.y) / axes.halfMinor;
    error += Math.abs(Math.hypot(u, v) - 1);
  }

  // A hexagon is round enough to fool a loose fit, so the fit is not loose. The
  // corner count then settles what is left: curves bend, shapes turn.
  const roundness = scoreFromError(error / path.loop.length, 0.03, 0.1);
  const corners = findCorners(path.loop, CORNER_MIN_TURN, true);
  const smoothness = scoreFromError(corners.length, 2, 5);
  const confidence = roundness * smoothness;
  if (confidence <= 0) return null;

  const isCircle = axes.halfMajor / axes.halfMinor <= CIRCLE_MAX_ASPECT;
  const evenRadius = (axes.halfMajor + axes.halfMinor) / 2;
  const radiusX = isCircle ? evenRadius : axes.halfMajor;
  const radiusY = isCircle ? evenRadius : axes.halfMinor;

  return {
    shapeType: isCircle ? 'circle' : 'ellipse',
    confidence,
    geometry: {
      points: axisPoints(axes.centre, axes.major, axes.minor, radiusX, radiusY),
      centre: axes.centre,
      radiusX,
      radiusY,
      rotation: isCircle ? 0 : axes.rotation,
    },
  };
}
